import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  RoutingPolicyMode,
  RoutingSkuTargetRecord,
  SkuSupplyCandidate,
} from '@nadoutong/contracts';
import {
  TENANT_ROUTING_KEY,
  useDeleteRoutingDefaultSkuTarget,
  useRoutingDefaultSkuPolicy,
  useSetRoutingDefaultSkuPolicy,
  useSetRoutingDefaultSkuTarget,
} from '../../api/useTenantRouting.ts';
import { useT } from '../../i18n/index.ts';
import type { SkuOption } from '../../pages/tenantRoutingShared.ts';
import {
  skuTargetCapBody,
  type SkuTargetDialogSubmission,
} from '../../pages/TenantRoutingTargetDialog.tsx';
import { coerceErrorMessage, useToast } from '../Toast.tsx';
import type { BoundRowSubmission } from './BoundSupplyRow.tsx';

/**
 * Every write the supply panel performs, in one place (#1428).
 *
 * The panel component owns dialog state and layout; this owns the mutations
 * and the one rule they all share:
 *
 * **The SKU fields come from the SKU, the physical identity comes from the
 * candidate or the stored row, and the two are not the same thing.** A
 * capability may serve a cross-model pin — `oauth/claude-sonnet-5` buying a
 * different upstream model is a first-class intent since #1329, not a bug to
 * correct — so reading `sku_name` / `vendor` / `model` off a candidate would
 * bind traffic to the wrong catalog identity and bill it wrong.
 */

export interface SupplyPanelWrites {
  /** Any target write in flight — disables row controls across the panel. */
  readonly saving: boolean;
  readonly deleting: boolean;
  /**
   * Spelled out rather than shortened to the obvious verb, which
   * `scripts/check-postgres-native.mjs` reserves for the retired embedded
   * database statement contract. A repo-wide guard should not be weakened to
   * accommodate one call site.
   */
  readonly bindCandidate: (candidate: SkuSupplyCandidate) => void;
  readonly saveBound: (target: RoutingSkuTargetRecord, submission: BoundRowSubmission) => void;
  /** Resolves true when the add-target dialog may close. */
  readonly addTarget: (submission: SkuTargetDialogSubmission) => Promise<boolean>;
  /** Resolves true when the pin dialog may close. */
  readonly pinCapability: (
    target: RoutingSkuTargetRecord,
    capabilityId: string,
  ) => Promise<boolean>;
  /** Resolves true when the confirm dialog may close. */
  readonly deleteTarget: (target: RoutingSkuTargetRecord) => Promise<boolean>;
  readonly policy: {
    readonly draft: RoutingPolicyMode;
    readonly setDraft: (mode: RoutingPolicyMode) => void;
    readonly save: () => void;
    readonly saveDisabled: boolean;
  };
}

export function useSupplyPanelWrites(sku: SkuOption): SupplyPanelWrites {
  const t = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const setTarget = useSetRoutingDefaultSkuTarget();
  const deleteTargetMut = useDeleteRoutingDefaultSkuTarget();
  const policyQ = useRoutingDefaultSkuPolicy(sku.skuId);
  const setPolicy = useSetRoutingDefaultSkuPolicy();

  const savedPolicyMode = policyQ.data?.policy_mode ?? 'full_chain';
  const [policyDraft, setPolicyDraft] = useState<RoutingPolicyMode>(savedPolicyMode);
  useEffect(() => {
    setPolicyDraft(savedPolicyMode);
  }, [savedPolicyMode]);

  function bindCandidate(candidate: SkuSupplyCandidate): void {
    setTarget.mutate(
      {
        skuId: sku.skuId,
        body: {
          // The SKU sells this identity; the candidate may buy another model.
          sku_name: sku.skuName,
          vendor: sku.vendor,
          model: sku.model,
          capability_id: candidate.capability_id,
          upstream_id: candidate.upstream_id,
          account_id: candidate.account_id,
          key_bucket_id: candidate.key_bucket_id,
          upstream_channel_id: candidate.upstream_channel_id,
          // Always a NEW row: the one case where this PUT could have matched an
          // existing one — an unpinned row on the same four-tuple, which
          // `upsertSkuTarget` finds via `(capability_id = $n OR capability_id
          // IS NULL)` — is diverted to the pin flow by `handleBind`'s guard
          // before reaching here. So this is the new row's default, not an
          // overwrite of anything an operator chose.
          enabled: true,
          // `weight` is deliberately omitted, and the two upsert branches want
          // opposite things. A brand-new row defaults to 0, which still routes:
          // with no weighted sibling the selector falls back to retry-chain
          // order, and with one it stays reserve until the bound group shapes
          // it. Re-binding an existing row preserves whatever weight it already
          // carried, which sending an explicit 0 would silently destroy.
        },
      },
      {
        // The candidate list and the readiness bits are refreshed by the
        // mutation hook itself (`invalidateSkuTargetQueries`), so every writer
        // of a default SKU target gets it — including the deletes below, which
        // flip the same derived state the other way.
        onSuccess: () => toast.success(t('sku_detail.supply.toast_bound')),
        onError: (error) => toast.error(coerceErrorMessage(error)),
      },
    );
  }

  /**
   * In-place edit of an already-bound row. The identity fields come from the
   * stored target verbatim — this write tunes weight / retry order / enabled
   * and must not be able to move the binding to different supply.
   */
  function saveBound(target: RoutingSkuTargetRecord, submission: BoundRowSubmission): void {
    if (target.capability_id === null) return;
    setTarget.mutate(
      {
        skuId: sku.skuId,
        body: {
          // SKU identity from the SKU, physical identity from the stored row.
          sku_name: sku.skuName,
          vendor: sku.vendor,
          model: sku.model,
          capability_id: target.capability_id,
          upstream_id: target.upstream_id,
          account_id: target.account_id,
          key_bucket_id: target.key_bucket_id,
          upstream_channel_id: target.upstream_channel_id,
          weight: submission.weight,
          retry_order: submission.retry_order,
          enabled: submission.enabled,
        },
      },
      {
        onSuccess: () => toast.success(t('tenant_routing.pool.toast_saved')),
        onError: (error) => toast.error(coerceErrorMessage(error)),
      },
    );
  }

  /**
   * Cleanup half of a PUT-then-DELETE pair. A failure here leaves a duplicate
   * row the operator must see, so it warns loudly and forces a refetch rather
   * than reporting success.
   */
  async function deletePrevious(previousId: string): Promise<boolean> {
    try {
      await deleteTargetMut.mutateAsync({ skuId: sku.skuId, targetId: previousId });
      return true;
    } catch {
      toast.error(t('tenant_routing.sku_pools.stale_row_warning', { id: previousId }));
      void qc.invalidateQueries({ queryKey: TENANT_ROUTING_KEY });
      return false;
    }
  }

  /**
   * Manual add-target, from the #909 dialog. **Not guaranteed to be an
   * insert.**
   *
   * Whether a PUT inserts or updates is decided by the server, not by what
   * editors this panel offers: `upsertSkuTarget`'s
   * `(capability_id = $n OR capability_id IS NULL)` lookup can still match an
   * unpinned row on the submitted four-tuple and UPDATE it in place — the same
   * hazard `handleBind` diverts to the pin flow. `TenantRoutingTargetDialog`
   * filters only by four-tuple + vendor + liveness and knows nothing about
   * this SKU's existing targets, so an operator can assemble such a four-tuple
   * by hand; the submitted `weight` (draft default `'100'`) would then
   * overwrite whatever that row carried.
   *
   * The guard is `SupplyPanel`'s `handleAddTarget`, not this function (#1431):
   * it needs the target list AND the pin dialog's state, both of which live in
   * the panel, exactly as they do for `handleBind`. Nothing else may call this
   * — an unguarded caller reintroduces the whole defect.
   */
  async function addTarget(submission: SkuTargetDialogSubmission): Promise<boolean> {
    try {
      await setTarget.mutateAsync({
        skuId: sku.skuId,
        body: {
          capability_id: submission.capability_id,
          sku_name: sku.skuName,
          vendor: sku.vendor,
          model: sku.model,
          upstream_id: submission.upstream_id,
          account_id: submission.account_id,
          key_bucket_id: submission.key_bucket_id,
          upstream_channel_id: submission.upstream_channel_id,
          weight: submission.weight,
          retry_order: submission.retry_order,
          enabled: submission.enabled,
          ...(submission.disabled_note !== undefined ? { disabled_note: submission.disabled_note } : {}),
          ...skuTargetCapBody(submission),
        },
      });
    } catch (error) {
      toast.error(t('tenant_routing.sku_pools.save_failed', {
        sku: sku.skuName,
        message: coerceErrorMessage(error),
      }));
      return false;
    }
    toast.success(t('tenant_routing.pool.toast_saved'), {
      action: {
        label: t('tenant_routing.toast.action.view_upstream'),
        to: `/supply/upstreams/${encodeURIComponent(submission.upstream_id)}`,
      },
    });
    return true;
  }

  /**
   * Pin a legacy row: PUT the same four-tuple with the chosen capability, then
   * drop the old unpinned row.
   *
   * The `previous.id !== writtenId` guard is not cosmetic. `upsertSkuTarget`
   * looks the row up with `(capability_id = $n OR capability_id IS NULL)`, so
   * the pin may land as an UPDATE of the very row being replaced — deleting
   * `previous.id` unconditionally would then delete the row just written and
   * leave the SKU with no supply at all.
   */
  async function pinCapability(
    previous: RoutingSkuTargetRecord,
    capabilityId: string,
  ): Promise<boolean> {
    let writtenId: string;
    try {
      const response = await setTarget.mutateAsync({
        skuId: sku.skuId,
        body: {
          // SKU identity from the SKU; four-tuple from the row being repaired.
          sku_name: sku.skuName,
          vendor: sku.vendor,
          model: sku.model,
          capability_id: capabilityId,
          upstream_id: previous.upstream_id,
          account_id: previous.account_id,
          key_bucket_id: previous.key_bucket_id,
          upstream_channel_id: previous.upstream_channel_id,
          // The server snapshot, not a draft: an unpinned row's tuning inputs
          // are read-only precisely because this write cannot carry them.
          weight: previous.weight,
          retry_order: previous.retry_order,
          enabled: previous.enabled,
        },
      });
      writtenId = response.target.id;
    } catch (error) {
      toast.error(t('tenant_routing.sku_pools.save_failed', {
        sku: sku.skuName,
        message: coerceErrorMessage(error),
      }));
      return false;
    }
    if (previous.id !== writtenId && !await deletePrevious(previous.id)) {
      return true;
    }
    toast.success(t('sku_detail.supply.toast_pinned'));
    return true;
  }

  async function deleteTarget(target: RoutingSkuTargetRecord): Promise<boolean> {
    try {
      await deleteTargetMut.mutateAsync({ skuId: sku.skuId, targetId: target.id });
    } catch (error) {
      toast.error(coerceErrorMessage(error));
      return false;
    }
    toast.success(t('tenant_routing.pool.toast_deleted'));
    return true;
  }

  function savePolicy(): void {
    setPolicy.mutate(
      {
        skuId: sku.skuId,
        body: {
          sku_name: sku.skuName,
          vendor: sku.vendor,
          model: sku.model,
          policy_mode: policyDraft,
        },
      },
      {
        onSuccess: () => toast.success(t('tenant_routing.toast.policy_saved')),
        onError: (error) => toast.error(coerceErrorMessage(error)),
      },
    );
  }

  return {
    saving: setTarget.isPending || deleteTargetMut.isPending,
    deleting: deleteTargetMut.isPending,
    bindCandidate,
    saveBound,
    addTarget,
    pinCapability,
    deleteTarget,
    policy: {
      draft: policyDraft,
      setDraft: setPolicyDraft,
      save: savePolicy,
      saveDisabled: setPolicy.isPending || policyQ.isPending || policyDraft === savedPolicyMode,
    },
  };
}
